import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface TopBarProps {
  sidebarExpanded?: boolean;
  setSidebarExpanded?: (expanded: boolean) => void;
  onShowLogin?: (mode?: 'login' | 'register') => void;
}

const TopBar: React.FC<TopBarProps> = ({ sidebarExpanded, setSidebarExpanded, onShowLogin }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollY, setScrollY] = React.useState(0);
  const [isTopBarHidden, setIsTopBarHidden] = React.useState(false);
  const [showUserDropdown, setShowUserDropdown] = React.useState(false);



  // Handle scroll for smooth TopBar transitions
  React.useEffect(() => {
    let lastY = 0;
    let upDistance = 0;
    let isHidden = false;

    const handleScroll = () => {
      const currentY = window.scrollY;

      if (currentY > lastY) {
        // Scrolling down - reset upward scroll distance and mark as hidden
        upDistance = 0;
        if (currentY > 50 && !isHidden) {
          isHidden = true;
          setIsTopBarHidden(true);
        }
      } else if (currentY < lastY) {
        // Scrolling up - accumulate upward scroll distance
        const scrollDiff = lastY - currentY;
        upDistance += scrollDiff;

        // Only show TopBar after accumulating 300px of upward scroll AND it was previously hidden
        if (upDistance >= 300 && isHidden) {
          isHidden = false;
          setIsTopBarHidden(false);
        }
      }

      setScrollY(currentY);
      lastY = currentY;
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, []);

  // Calculate smooth TopBar transform and opacity based on scroll
  const getTopBarStyle = () => {
    const hideDistance = window.innerWidth < 768 ? 120 : 64;

    // Only show at the very top of the page (before any hiding has occurred)
    if (scrollY < 10) {
      return {
        transform: 'translateY(0px)',
        opacity: 1,
      };
    }

    // Use the hidden state to determine visibility for all other scroll positions
    if (isTopBarHidden) {
      return {
        transform: `translateY(-${hideDistance}px)`,
        opacity: 0,
      };
    } else {
      return {
        transform: 'translateY(0px)',
        opacity: 1,
      };
    }
  };

  const topBarStyle = getTopBarStyle();

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[80] transition-all duration-300 ease-out"
      style={{
        transform: topBarStyle.transform,
        opacity: topBarStyle.opacity,
      }}
    >
      {/* Desktop: Single Bar with everything */}
      <div className="hidden lg:block h-16 bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 backdrop-blur-xl border-b border-white/10 shadow-lg">
        <div className="h-full flex items-center justify-between px-6">
          {/* Left side - Hamburger and Logo */}
          <div className="flex items-center gap-6">
            {/* Hamburger menu */}
            {setSidebarExpanded && (
              <button
                onClick={() => setSidebarExpanded(!sidebarExpanded)}
                className="p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm border border-white/10"
              >
                <svg className={`w-5 h-5 transition-transform duration-500 ${sidebarExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Logo */}
            <div className="flex items-center">
              <h1 className="text-2xl font-display font-bold text-white">LoveChat</h1>
              <span className="text-accent-pink text-2xl font-display font-bold">.ai</span>
            </div>

            {/* Desktop Navigation - Facebook style icons only */}
            <div className="flex items-center justify-center flex-1 max-w-2xl mx-auto">
              <div className="flex items-center gap-8">
                <button
                  onClick={() => navigate('/')}
                  className={`p-3 rounded-xl transition-all duration-300 relative ${location.pathname === '/'
                    ? 'text-pink-400'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                  </svg>
                  {location.pathname === '/' && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                  )}
                </button>

                <button
                  onClick={() => navigate('/conversations')}
                  className={`p-3 rounded-xl transition-all duration-300 relative ${location.pathname === '/conversations'
                    ? 'text-pink-400'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                  </svg>
                  {location.pathname === '/conversations' && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                  )}
                </button>

                <button
                  onClick={() => navigate('/character')}
                  className={`p-3 rounded-xl transition-all duration-300 relative ${location.pathname === '/character'
                    ? 'text-pink-400'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.5 7h-5c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5H15v6h2zm-11.5-6L10 8H8l-1.5 8zm-.5 0c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2z" />
                  </svg>
                  {location.pathname === '/character' && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right side - Login buttons */}
          <div className="flex items-center gap-3">
            {user ? (
              <div className="relative">
                {/* User Profile Button */}
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 transition-all duration-300"
                >
                  {/* User Avatar */}
                  <div className="w-8 h-8 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  {/* User Name */}
                  <span className="text-white text-sm font-medium hidden md:block">
                    {user.full_name || user.email || 'User'}
                  </span>
                  {/* Dropdown Arrow */}
                  <svg className={`w-4 h-4 text-white/60 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showUserDropdown && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-[150]"
                      onClick={() => setShowUserDropdown(false)}
                    />

                    {/* Dropdown Content */}
                    <div className="fixed right-6 top-16 mt-2 w-80 bg-dark-800 border border-dark-600 rounded-2xl shadow-2xl z-[160] overflow-hidden">
                      {/* User Info Header */}
                      <div className="p-4 border-b border-dark-600">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                            <span className="text-white text-lg font-bold">
                              {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-white font-semibold">
                              {user.full_name || 'User'}
                            </h3>
                            <p className="text-dark-300 text-sm">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setShowUserDropdown(false);
                            if (user) {
                              navigate(`/user/${user.id}`);
                            }
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-dark-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Profile</span>
                        </button>

                        <button
                          onClick={() => {
                            setShowUserDropdown(false);
                            // Navigate to settings when implemented
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-dark-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Settings & Privacy</span>
                        </button>

                        <button
                          onClick={() => {
                            setShowUserDropdown(false);
                            // Navigate to help when implemented
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-dark-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Help & Support</span>
                        </button>

                        {/* Divider */}
                        <div className="border-t border-dark-600 my-2"></div>

                        {/* Logout */}
                        <button
                          onClick={() => {
                            setShowUserDropdown(false);
                            logout();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Log Out</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : onShowLogin ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => onShowLogin('login')}
                  className="px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-xl border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
                >
                  Login
                </button>
                <button
                  onClick={() => onShowLogin('register')}
                  className="px-4 py-2.5 bg-gradient-to-r from-accent-purple to-accent-pink text-white text-sm font-semibold rounded-xl shadow-lg shadow-accent-purple/25 hover:shadow-xl hover:shadow-accent-purple/40 transition-all duration-300 transform hover:scale-105 border border-accent-purple/30"
                >
                  Join Free
                </button>
              </div>
            ) : (
              <a
                href="/"
                className="px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-xl border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
              >
                Login
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: Two-Bar System */}
      <div className="block lg:hidden">
        {/* Mobile Top Bar - Logo + Login */}
        <div className="h-16 bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 backdrop-blur-xl border-b border-white/10 shadow-lg">
          <div className="h-full flex items-center justify-between px-4">
            {/* Left side - Hamburger and Logo */}
            <div className="flex items-center gap-3">
              {/* Hamburger menu */}
              {setSidebarExpanded && (
                <button
                  onClick={() => setSidebarExpanded(!sidebarExpanded)}
                  className="p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm border border-white/10"
                >
                  <svg className={`w-5 h-5 transition-transform duration-500 ${sidebarExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              {/* Logo */}
              <div className="flex items-center">
                <h1 className="text-xl font-display font-bold text-white">LoveChat</h1>
                <span className="text-accent-pink text-xl font-display font-bold">.ai</span>
              </div>
            </div>

            {/* Right side - Login buttons */}
            <div className="flex items-center gap-2">
              {user ? (
                <div className="relative">
                  {/* Mobile User Profile Button */}
                  <button
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="flex items-center gap-2 p-2 rounded-xl hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="w-7 h-7 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </button>


                </div>
              ) : onShowLogin ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onShowLogin('login')}
                    className="px-3 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-xl border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => onShowLogin('register')}
                    className="px-3 py-2 bg-gradient-to-r from-accent-purple to-accent-pink text-white text-sm font-semibold rounded-xl shadow-lg shadow-accent-purple/25 hover:shadow-xl hover:shadow-accent-purple/40 transition-all duration-300 transform hover:scale-105 border border-accent-purple/30"
                  >
                    Join Free
                  </button>
                </div>
              ) : (
                <a
                  href="/"
                  className="px-3 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-xl border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
                >
                  Login
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Bar - Facebook style icons only */}
        <div className="h-14 bg-dark-800/95 backdrop-blur-xl border-b border-white/5 relative z-10">
          <div className="h-full flex items-center justify-center px-4">
            <div className="flex items-center gap-8">
              <button
                onClick={() => navigate('/')}
                className={`p-3 rounded-xl transition-all duration-300 relative ${location.pathname === '/'
                  ? 'text-pink-400'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                </svg>
                {location.pathname === '/' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                )}
              </button>

              <button
                onClick={() => navigate('/conversations')}
                className={`p-3 rounded-xl transition-all duration-300 relative ${location.pathname === '/conversations'
                  ? 'text-pink-400'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                </svg>
                {location.pathname === '/conversations' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                )}
              </button>

              <button
                onClick={() => navigate('/character')}
                className={`p-3 rounded-xl transition-all duration-300 relative ${location.pathname === '/character'
                  ? 'text-pink-400'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.5 7h-5c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5H15v6h2zm-11.5-6L10 8H8l-1.5 8zm-.5 0c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2z" />
                </svg>
                {location.pathname === '/character' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 rounded-full"></div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown Menu - Outside all containers */}
      {showUserDropdown && user && (
        <div className="block lg:hidden">
          <div
            className="fixed inset-0 z-[200]"
            onClick={() => setShowUserDropdown(false)}
          />

          <div className="fixed right-4 top-20 w-72 bg-dark-800 border border-dark-600 rounded-2xl shadow-2xl z-[210] overflow-hidden">
            {/* User Info Header */}
            <div className="p-4 border-b border-dark-600">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                  <span className="text-white text-lg font-bold">
                    {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-white font-semibold">
                    {user.full_name || 'User'}
                  </h3>
                  <p className="text-dark-300 text-sm">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <button
                onClick={() => {
                  setShowUserDropdown(false);
                  if (user) {
                    navigate(`/user/${user.id}`);
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-dark-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Profile</span>
              </button>

              <button
                onClick={() => setShowUserDropdown(false)}
                className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-dark-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Settings & Privacy</span>
              </button>

              <button
                onClick={() => setShowUserDropdown(false)}
                className="w-full flex items-center gap-3 px-4 py-3 text-white hover:bg-dark-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Help & Support</span>
              </button>

              <div className="border-t border-dark-600 my-2"></div>

              <button
                onClick={() => {
                  setShowUserDropdown(false);
                  logout();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBar;